import { useState } from "react";
import { Box, Button, Flex, Input, SimpleGrid, Text } from "@chakra-ui/react";
import { CalendarIcon, ChevronDownIcon } from "@chakra-ui/icons";
import moment from "moment";
import { VARIANT } from "../constants/colors";
import { customColors } from "../atoms/customColors";
import { useRecoilState } from "recoil";

// maxDias e opcional: sem ele o componente segue exatamente como antes, sem
// limite — e o que as outras telas que ja usam este dropdown esperam. Quem
// precisa de teto (tela sem paginacao, export da API) passa o numero de dias.
export default function DateDropdown({
  filtros,
  setFiltros,
  maxDias,
  label = "Data",
  futurePresets = false,
}: any) {
  const [open, setOpen] = useState(false);
  const [colors] = useRecoilState(customColors);
  const [filtroInterno, setFiltroInterno] = useState({
    initialDateFilter:
      filtros.initialDateFilter ||
      moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS"),
    finalDateFilter:
      filtros.finalDateFilter ||
      moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS"),
  });

  const diasSelecionados = moment(filtroInterno.finalDateFilter).diff(
    moment(filtroInterno.initialDateFilter),
    "days"
  );
  const excedeuLimite = maxDias != null && diasSelecionados > maxDias;

  function mudarData() {
    setFiltros({
      ...filtros,
      initialDateFilter: filtroInterno.initialDateFilter,
      finalDateFilter: filtroInterno.finalDateFilter,
    });
  }

  function selecionarProximosDias(dias: number) {
    setFiltroInterno({
      initialDateFilter: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS"),
      finalDateFilter: moment().add(dias, "days").endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS"),
    });
  }

  return (
    <Box position="relative">
      <Button
        minWidth="290px"
        minHeight="40px"
        justifyContent="space-between"
        bg={colors.WHITE}
        color={colors.BLUEMONEDDBACKGROUND}
        borderWidth="1px"
        borderColor={
          filtros.initialDateFilter
            ? colors.BLUEMONEDDBUTTONS
            : colors.GRAY_LIGHTER
        }
        borderRadius="8px"
        fontWeight="medium"
        leftIcon={<CalendarIcon color={colors.BLUEMONEDDBUTTONS} />}
        rightIcon={
          <ChevronDownIcon
            boxSize={5}
            transform={open ? "rotate(180deg)" : undefined}
          />
        }
        onClick={() => setOpen(!open)}
        _hover={{
          borderColor: colors.BLUEMONEDDBUTTONS,
          bg: colors.GRAY_LIGHTEST,
        }}
      >
        {filtros.initialDateFilter && filtros.finalDateFilter
          ? `${moment(filtros.initialDateFilter).format("DD/MM/YYYY")} — ${moment(
            filtros.finalDateFilter
          ).format("DD/MM/YYYY")}`
          : `Filtrar por ${label}`}
      </Button>

      {open && (
        <Box
          position="absolute"
          bgColor="white"
          borderRadius="12px"
          borderWidth="1px"
          borderColor={colors.GRAY_LIGHTER}
          boxShadow="0 12px 32px rgba(0, 0, 0, 0.14)"
          p={5}
          mt={2}
          minWidth="360px"
          zIndex={1000}
        >
          <Flex direction="column" gap={4}>
            <Box>
              <Text fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
                {label}
              </Text>
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Selecione um período rápido ou escolha as datas.
              </Text>
            </Box>

            {futurePresets ? (
              <Flex direction="column" gap={3}>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor={colors.BLUEMONEDDBUTTONS}
                  color={colors.BLUEMONEDDBACKGROUND}
                  onClick={() => selecionarProximosDias(0)}
                >
                  Hoje
                </Button>

                <Box>
                  <Text fontSize="xs" fontWeight="bold" color={colors.GRAY_DARK} mb={2}>
                    PERÍODOS ANTERIORES
                  </Text>
                  <SimpleGrid columns={2} spacing={2}>
                    {[7, 15, 30].map((dias) => (
                      <Button
                        key={`anteriores-${dias}`}
                        size="sm"
                        minH="36px"
                        height="auto"
                        px={2}
                        py={2}
                        whiteSpace="normal"
                        lineHeight="short"
                        textAlign="center"
                        variant="outline"
                        borderColor={colors.GRAY_LIGHTER}
                        color={colors.BLUEMONEDDBACKGROUND}
                        onClick={() =>
                          setFiltroInterno({
                            initialDateFilter: moment()
                              .subtract(dias, "days")
                              .startOf("day")
                              .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                            finalDateFilter: moment()
                              .endOf("day")
                              .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                          })
                        }
                      >
                        Últimos {dias} dias
                      </Button>
                    ))}
                  </SimpleGrid>
                </Box>

                <Box>
                  <Text fontSize="xs" fontWeight="bold" color={colors.GRAY_DARK} mb={2}>
                    PRÓXIMOS VENCIMENTOS
                  </Text>
                  <SimpleGrid columns={2} spacing={2}>
                    {[7, 15, 30].map((dias) => (
                      <Button
                        key={`proximos-${dias}`}
                        size="sm"
                        minH="36px"
                        height="auto"
                        px={2}
                        py={2}
                        whiteSpace="normal"
                        lineHeight="short"
                        textAlign="center"
                        variant="outline"
                        borderColor={colors.GRAY_LIGHTER}
                        color={colors.BLUEMONEDDBACKGROUND}
                        onClick={() => selecionarProximosDias(dias)}
                        _hover={{
                          bg: colors.GRAY_LIGHTEST,
                          borderColor: colors.BLUEMONEDDBUTTONS,
                        }}
                      >
                        Próximos {dias} dias
                      </Button>
                    ))}
                  </SimpleGrid>
                </Box>
              </Flex>
            ) : (
              <SimpleGrid columns={2} spacing={2}>
                {[
                  { dias: 0, texto: "Hoje" },
                  { dias: 1, texto: "Ontem" },
                  { dias: 7, texto: "Últimos 7 dias" },
                  { dias: 15, texto: "Últimos 15 dias" },
                  { dias: 30, texto: "Últimos 30 dias" },
                ].map((periodo) => (
                  <Button
                    key={periodo.texto}
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setFiltroInterno({
                        initialDateFilter: moment()
                          .subtract(periodo.dias, "days")
                          .startOf("day")
                          .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        finalDateFilter: moment()
                          .subtract(periodo.texto === "Ontem" ? 1 : 0, "days")
                          .endOf("day")
                          .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                      })
                    }
                  >
                    {periodo.texto}
                  </Button>
                ))}
              </SimpleGrid>
            )}

            <Flex direction="column" gap={2} mt={4}>
              <Text fontWeight="bold">De</Text>
              <Input
                type="date"
                value={moment(filtroInterno.initialDateFilter).format(
                  "YYYY-MM-DD"
                )}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setFiltroInterno({
                    ...filtroInterno,
                    initialDateFilter: moment(newDate)
                      .startOf("day")
                      .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                  });
                }}
              />
            </Flex>

            <Flex direction="column" gap={2} mt={4}>
              <Text fontWeight="bold">Até</Text>
              <Input
                type="date"
                value={moment(filtroInterno.finalDateFilter).format(
                  "YYYY-MM-DD"
                )}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setFiltroInterno({
                    ...filtroInterno,
                    finalDateFilter: moment(newDate)
                      .endOf("day")
                      .format("YYYY-MM-DDTHH:mm:ss.SSS"),
                  });
                }}
              />
            </Flex>

            {excedeuLimite ? (
              <Text color="red.500" fontSize="0.75rem" mt={2}>
                Período de {diasSelecionados} dias. O máximo é {maxDias} dias —
                ajuste as datas para consultar.
              </Text>
            ) : null}

            <Flex w="100%" justifyContent="flex-end" gap={2} mt={2}>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button
                bgColor={colors.BLUEMONEDDBUTTONS || VARIANT}
                color="white"
                px={7}
                isDisabled={excedeuLimite}
                onClick={() => {
                  setOpen(false);
                  mudarData();
                }}
              >
                Filtrar
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}
    </Box>
  );
}
